import { useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  DataList,
  EmptyState,
  LoadingBlock,
  PageHeader,
  SelectField,
  StatusBadge,
  type DataColumn,
} from "@nahal/ui";
import type { Plan } from "@nahal/api-client";
import { usePlans, useProducts } from "../../lib/queries";

/**
 * Read-only on purpose. Adding a product or a plan is a Flyway migration, not a
 * form -- the catalog is what both consoles read to decide what exists, so a
 * typo made at 2am in a text box would reshape every customer's console.
 */
export function CatalogScreen() {
  const products = useProducts();
  const [productCode, setProductCode] = useState("");
  const plans = usePlans(productCode || undefined);

  if (products.isLoading) return <LoadingBlock />;

  const planColumns: Array<DataColumn<Plan>> = [
    { key: "name", header: "Plan", primary: true, cell: (row) => row.name },
    { key: "product", header: "Product", cell: (row) => row.productCode },
    { key: "code", header: "Code", desktopOnly: true, cell: (row) => row.code },
    {
      key: "entitlements",
      header: "Entitlements",
      cell: (row) => {
        const entries = Object.entries(row.entitlements);
        if (entries.length === 0) return "none";
        return (
          <span className="text-xs text-[var(--text-secondary)]">
            {entries.map(([key, value]) => `${key}=${value}`).join(", ")}
          </span>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Catalog & plans"
        description="What the consoles offer. Changing it is a migration, not a form."
      />

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {products.data?.map((product) => (
            <Card key={product.code}>
              <CardBody>
                <div className="flex items-start justify-between gap-2">
                  <h2 className="text-sm font-semibold text-[var(--text-primary)]">
                    {product.name}
                  </h2>
                  <StatusBadge
                    status={product.available ? "available" : "hidden"}
                    tone={product.available ? "success" : "neutral"}
                  />
                </div>
                {product.tagline && (
                  <p className="mt-1 text-sm text-[var(--text-secondary)]">{product.tagline}</p>
                )}
                <dl className="mt-3 space-y-1 text-xs text-[var(--text-muted)]">
                  <div>
                    <dt className="inline">code: </dt>
                    <dd className="inline">{product.code}</dd>
                  </div>
                  {product.appUrl && (
                    <div className="truncate">
                      <dt className="inline">app: </dt>
                      <dd className="inline">{product.appUrl}</dd>
                    </div>
                  )}
                </dl>
              </CardBody>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader title="Plans" />
          <CardBody>
            <SelectField
              label="Product"
              options={[
                { value: "", label: "All products" },
                ...(products.data ?? []).map((product) => ({
                  value: product.code,
                  label: product.name,
                })),
              ]}
              value={productCode}
              onChange={(event) => setProductCode(event.target.value)}
            />
          </CardBody>
          <DataList
            columns={planColumns}
            rows={plans.data ?? []}
            rowKey={(row) => row.id}
            loading={plans.isLoading}
            error={plans.isError ? "Could not load plans." : null}
            empty={{ title: "No plans", description: "Nothing is packaged for this product yet." }}
          />
        </Card>

        {(products.data?.length ?? 0) === 0 && <EmptyState title="Catalog is empty" />}
      </div>
    </>
  );
}
